import React from 'react';
import { useSpc } from '../../context/SpcContext';
import { ExternalLink, Pencil } from 'lucide-react';
import { ImageGallery } from '../ImageGallery';

export default function Benefits({ go }) {
  const { state, role, openModal, modal, removeDiscount, openSPC, copy, getBenefits } = useSpc();
  const [photoMap, setPhotoMap] = React.useState(null);
  const fetchPhotos = React.useCallback(async () => {
    try {
      const { data } = await getBenefits();
      const toMap = (arr) => Object.fromEntries((arr || []).map(x => [x.id, { photos: x.photos || [], logo: x.logo || '' }]));
      setPhotoMap({ discounts: toMap(data.discounts), services: toMap(data.services) });
    } catch (e) { /* photo galleries are a lazy enhancement — Edit stays guarded below on failure */ }
  }, [getBenefits]);
  React.useEffect(() => { fetchPhotos(); }, [fetchPhotos]);
  const prevModalType = React.useRef(null);
  React.useEffect(() => {
    if (modal) { prevModalType.current = modal.type; return; }
    if (prevModalType.current === 'newDiscount') fetchPhotos();
    prevModalType.current = null;
  }, [modal, fetchPhotos]);
  const withPhotos = (arr, key) => arr.map(x => {
    const extra = photoMap?.[key]?.[x.id];
    if (!extra) return x;
    return { ...x, photos: extra.photos.length ? extra.photos : x.photos, logo: extra.logo || x.logo };
  });
  const discounts = withPhotos(state.discounts, 'discounts');
  const services = withPhotos(state.services, 'services');
  const photosReady = photoMap !== null;
  const [discountFilter, setDiscountFilter] = React.useState('all'); // all | discount | partner
  const [discountSort, setDiscountSort] = React.useState('newest'); // newest | oldest | az
  const visibleDiscounts = discounts
    .filter(d => {
      if (discountFilter === 'all') return true;
      const isPartner = d.kind === 'partner' || (!d.code && d.url);
      return discountFilter === 'partner' ? isPartner : !isPartner;
    })
    .sort((a, b) => {
      if (discountSort === 'az') return (a.partner || '').localeCompare(b.partner || '');
      return discountSort === 'oldest' ? a.id - b.id : b.id - a.id;
    });
  return (
    <>
      <div className="section-head">
        <div><h2>Rewards & benefits</h2><p>Member-only perks — discounts, partners and club services.</p></div>
        {role === 'admin' && <div style={{ display: 'flex', gap: 8 }}><button className="btn secondary" data-testid="add-offer-btn" onClick={() => openModal('newDiscount')}>+ Offer</button></div>}
      </div>

      <div className="section-head"><div><div className="eyebrow">Member only</div><h2>Discounts &amp; partners</h2><p>Discount codes and affiliate partners that make SPC membership worth more.</p></div></div>
      <div className="discount-filters" data-testid="discount-filters" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {[['all', 'All'], ['discount', 'Discount codes'], ['partner', 'Partners']].map(([val, label]) => (
            <button key={val} type="button" className={`btn ${discountFilter === val ? '' : 'secondary'}`} style={{ padding: '6px 14px', fontSize: 13 }} onClick={() => setDiscountFilter(val)} data-testid={`discount-filter-${val}`}>{label}</button>
          ))}
        </div>
        <select value={discountSort} onChange={(e) => setDiscountSort(e.target.value)} style={{ marginLeft: 'auto' }} data-testid="discount-sort-select">
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="az">A–Z</option>
        </select>
      </div>
      <div className="grid">
        {!visibleDiscounts.length && <div className="empty" data-testid="discount-filter-empty">No {discountFilter === 'all' ? 'offers' : discountFilter === 'partner' ? 'partners' : 'discount codes'} yet.</div>}
        {visibleDiscounts.map(d => {
          const isPartner = d.kind === 'partner' || (!d.code && d.url);
          return (
            <div className="card span-4" key={d.id}>
              {d.photos && d.photos.length > 0 && <ImageGallery photos={d.photos} alt={d.partner} testId={`discount-gallery-${d.id}`} />}
              <div className="partner-head">
                {d.logo
                  ? <img src={d.logo} alt={d.partner} className="partner-logo" data-testid={`partner-logo-${d.id}`} />
                  : <div className="partner-logo partner-logo-fallback" aria-hidden="true">{(d.partner || 'S').slice(0, 2).toUpperCase()}</div>}
                <span className={`pill ${isPartner ? 'green' : 'yellow'}`}>{isPartner ? 'SPC Partner' : 'Member code'}</span>
              </div>
              <h3 style={{ marginTop: 12 }}>{d.partner}</h3>
              <p>{d.offer}</p>
              {!isPartner && d.code && <div className="discount-code" data-testid={`discount-code-${d.id}`}>{d.code}</div>}
              {role === 'admin' ? (
                <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                  {d.url && <a className="btn secondary" href={d.url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>Visit <ExternalLink size={13} /></a>}
                  <button className="btn secondary" disabled={!photosReady} title={!photosReady ? 'Loading photos…' : ''} onClick={() => openModal('newDiscount', d)} data-testid={`edit-discount-${d.id}`}><Pencil size={13} /> {photosReady ? 'Edit' : 'Loading…'}</button>
                  <button className="btn danger" onClick={() => removeDiscount(d.id)} data-testid={`remove-discount-${d.id}`}>Remove</button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                  {d.code && <button className="btn secondary" onClick={() => copy(d.code, 'Code copied')}>Copy code</button>}
                  {d.url && (
                    <a
                      className="btn"
                      href={d.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                      data-testid={`discount-visit-${d.id}`}
                    >
                      Visit partner <ExternalLink size={14} />
                    </a>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="section-head"><div><div className="eyebrow">Explore</div><h2>Explore SPC</h2><p>Your coaching, plans, services, camps and kit in one member-only home.</p></div></div>
      <div className="grid">
        {services.map(s => (
          <div className="card span-4" key={s.id}>
            {s.photos && s.photos.length > 0
              ? <ImageGallery photos={s.photos} alt={s.title} testId={`service-gallery-${s.id}`} />
              : <div style={{ fontSize: 34 }}>{s.icon}</div>}
            <span className="pill yellow" style={{ marginTop: 10 }}>{s.category}</span>
            <h3 style={{ marginTop: 10 }}>{s.title}</h3>
            <p>{s.description}</p>
            <div className="price">{s.price}</div>
            <button className="btn secondary" style={{ marginTop: 12 }} onClick={() => openSPC(s.link)}>Explore</button>
          </div>
        ))}
      </div>
    </>
  );
}
